import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';

/**
 * AUTH-02 — Public route `/verify-email?token=...`.
 * Validates the token via the backend and shows success / error states.
 * On success, also refreshes the logged-in user (if any) so the banner
 * disappears immediately.
 *
 * Module-level cache stores the IN-FLIGHT promise per token (not just the
 * resolved value). React 18 StrictMode fires the effect twice synchronously
 * before the first request resolves; deduping at the promise level prevents
 * the legitimate second call from hitting an already-consumed token.
 */
const _pendingVerifications = new Map(); // token -> Promise<true>

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const [state, setState] = useState('loading'); // loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setErrorMsg('Link inválido. Solicite um novo email de verificação.');
      setState('error');
      return;
    }
    let cancelled = false;
    // Dedupe by promise (StrictMode double-invoke / accidental double-click).
    // The backend ALSO caches recently-consumed tokens for 60s as a fallback.
    let pending = _pendingVerifications.get(token);
    if (!pending) {
      pending = api.verifyEmail(token);
      _pendingVerifications.set(token, pending);
    }
    (async () => {
      try {
        await pending;
        if (cancelled) return;
        if (user) await refreshUser();
        setState('success');
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setErrorMsg(
          typeof detail === 'string'
            ? detail
            : 'Este link de verificação não é mais válido. Solicite um novo email.'
        );
        setState('error');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wrapperBg = isDark ? 'bg-slate-900' : 'bg-slate-50';
  const cardBg = isDark
    ? 'bg-slate-800/60 border-slate-700 text-slate-100'
    : 'bg-white border-slate-200 text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-600';

  return (
    <div className={`min-h-screen flex items-center justify-center p-6 ${wrapperBg}`}>
      <div
        className={`max-w-md w-full rounded-2xl border p-8 text-center shadow-sm ${cardBg}`}
        data-testid="verify-email-card"
      >
        {state === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-primary" />
            <h1 className="text-xl font-semibold mb-2">Verificando seu email...</h1>
            <p className={`text-sm ${muted}`}>Aguarde um instante.</p>
          </>
        )}

        {state === 'success' && (
          <>
            <CheckCircle2
              className="w-14 h-14 mx-auto mb-4 text-emerald-500"
              data-testid="verify-email-success-icon"
            />
            <h1 className="text-2xl font-semibold mb-2" data-testid="verify-email-success-title">
              Email confirmado!
            </h1>
            <p className={`text-sm ${muted} mb-6`}>
              Sua conta está verificada. Você já pode gerar prontuários clínicos.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-primary hover:bg-primary-hover"
              data-testid="verify-email-go-dashboard"
            >
              Ir para o Dashboard
            </Button>
          </>
        )}

        {state === 'error' && (
          <>
            <XCircle
              className="w-14 h-14 mx-auto mb-4 text-red-500"
              data-testid="verify-email-error-icon"
            />
            <h1 className="text-2xl font-semibold mb-2" data-testid="verify-email-error-title">
              Link inválido ou expirado
            </h1>
            <p className={`text-sm ${muted} mb-6`}>{errorMsg}</p>
            <Button
              onClick={() => navigate(user ? '/dashboard' : '/login')}
              variant="outline"
              className="w-full"
              data-testid="verify-email-request-new"
            >
              {user ? 'Solicitar novo email' : 'Voltar para login'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
